/**
 * ListenChip — compact TTS chip for article cards on hub pages,
 * newsletter previews, and any list view where the full-width
 * <ListenToArticle /> player is too large.
 *
 * Behaviour:
 *   • Renders a small "▶ Listen · N min" pill.
 *   • On first click: kicks off /article/{slug}/tts/generate and
 *     expands into an inline <audio> player.
 *   • Uses the same MongoDB-backed cache, so repeat clicks are
 *     instant across every reader surface.
 *
 * Stops event propagation so it can live inside a wrapping <a> card
 * link (the writer stays on the hub while previewing the story).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Headphones, Loader2, AlertCircle, Pause, Play } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

export default function ListenChip({ slug, className = "", testIdPrefix = "listen-chip" }) {
  const [state, setState] = useState({ status: "unknown", meta: null, error: null });
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Probe cache on mount — silent (no network noise per card if none exists yet).
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/article/${slug}/tts/status`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        if (d.ready) setState({ status: "ready", meta: d, error: null });
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [slug]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const generateAndPlay = useCallback(async (e) => {
    stop(e);
    if (state.status === "ready") {
      setExpanded(true);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 30);
      return;
    }
    setState({ status: "loading", meta: null, error: null });
    setExpanded(true);
    try {
      const r = await fetch(`${BASE}/article/${slug}/tts/generate`, { method: "POST" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setState({ status: "ready", meta: d, error: null });
      setTimeout(() => audioRef.current?.play().catch(() => {}), 30);
    } catch (err) {
      setState({ status: "error", meta: null, error: err.message || "Audio unavailable" });
    }
  }, [slug, state.status]);

  const toggle = useCallback((e) => {
    stop(e);
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }, []);

  const durMin = state.meta?.duration_s
    ? Math.max(1, Math.round(state.meta.duration_s / 60))
    : null;

  if (state.status === "error") {
    return (
      <div
        className={`inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] text-amber-800 ${className}`}
        data-testid={`${testIdPrefix}-error`}
        onClick={stop}
      >
        <AlertCircle className="w-3 h-3" />
        Audio unavailable
      </div>
    );
  }

  if (expanded && state.status === "ready" && state.meta) {
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-1 pr-3 py-0.5 text-[11px] shadow-sm ${className}`}
        data-testid={`${testIdPrefix}-player`}
        onClick={stop}
      >
        <button
          type="button"
          onClick={toggle}
          className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
          data-testid={`${testIdPrefix}-toggle`}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        </button>
        <span className="uppercase tracking-widest font-semibold">
          {playing ? "Playing" : "Listen"}
        </span>
        {durMin && <span className="opacity-70">· {durMin} min</span>}
        <audio
          ref={audioRef}
          src={`${process.env.REACT_APP_BACKEND_URL || ""}${state.meta.mp3_url}`}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          data-testid={`${testIdPrefix}-audio`}
        />
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <div
        className={`inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[11px] text-slate-500 ${className}`}
        data-testid={`${testIdPrefix}-loading`}
        onClick={stop}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Generating…
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={generateAndPlay}
      className={`inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2.5 py-0.5 text-[11px] font-semibold hover:bg-slate-700 transition-colors ${className}`}
      data-testid={`${testIdPrefix}-listen`}
      title="Play a spoken version of this story"
    >
      <Headphones className="w-3 h-3" />
      Listen
      {durMin && <span className="opacity-70 font-normal">· {durMin} min</span>}
    </button>
  );
}
